import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiService';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import settingsService from '../services/settingsService';

// Import reusable components
import SalesHeader from '../components/sales/SalesHeader';
import SalesTabs from '../components/sales/SalesTabs';
import SalesFilters from '../components/sales/SalesFilters';
import SalesTable from '../components/sales/SalesTable';
import SalesDialogs from '../components/sales/SalesDialogs';
import PaymentDialog from '../components/sales/PaymentDialog';

function Sales() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
  const [selectedInvoiceToPrint, setSelectedInvoiceToPrint] = useState(null);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [viewInvoiceDialog, setViewInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewInvoiceLoading, setViewInvoiceLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [resubmittingInvoiceId, setResubmittingInvoiceId] = useState(null);
  const [phase2Enabled, setPhase2Enabled] = useState(false); // Add phase2Enabled state
  
  // Define export columns
  const exportColumns = useMemo(() => [
    { field: 'invoiceNo', headerName: 'Invoice #', width: 150 },
    { field: 'customerName', headerName: 'Customer', width: 200 },
    { field: 'invoiceDate', headerName: 'Date', width: 120 },
    { field: 'dueDate', headerName: 'Due Date', width: 120 },
    { field: 'totalAmt', headerName: 'Amount', width: 120 },
    { field: 'paymentStatus', headerName: 'Payment Status', width: 150 },
    { field: 'balanceAmt', headerName: 'Balance', width: 120 }
  ], []);

  // Memoize getExportData
  const getExportData = useMemo(() => {
    return invoices.map(invoice => ({
      invoiceNo: invoice.invoiceNo || '',
      customerName: invoice.customerName || 'Walk-in Customer',
      invoiceDate: invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'dd/MM/yyyy') : '',
      dueDate: invoice.dueDate ? format(new Date(invoice.dueDate), 'dd/MM/yyyy') : '',
      totalAmt: invoice.totalAmt ? Number(invoice.totalAmt).toFixed(2) : '0.00',
      paymentStatus: invoice.paymentStatus || '',
      balanceAmt: invoice.balanceAmt ? Number(invoice.balanceAmt).toFixed(2) : '0.00'
    }));
  }, [invoices]);

  const [filters, setFilters] = useState({
    paymentStatus: {
      paid: true,
      pending: true,
      overdue: true,
      draft: true,
    },
    dateRange: {
      start: null,
      end: null,
    },
    amountRange: {
      min: null,
      max: null,
    }
  });

  // Memoize fetchInvoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      let response;
      
      if (activeTab === 1) {
        response = await api.get('/draft-invoices', { params });
      } else {
        const activeStatuses = [];
        if (filters.paymentStatus.paid) activeStatuses.push('paid');
        if (filters.paymentStatus.pending) activeStatuses.push('pending');
        if (filters.paymentStatus.overdue) activeStatuses.push('overdue');
        if (filters.paymentStatus.draft) activeStatuses.push('draft');
        
        if (activeStatuses.length > 0 && activeStatuses.length < 4) {
          params.status = activeStatuses.join(',');
        }
        
        response = await api.get('/sales', { params });
      }
      
      if (response.data.success) {
        const formattedInvoices = response.data.data.map(invoice => ({
          id: invoice.invoiceNo,
          customer: invoice.customerName || 'Unknown Customer',
          mobileNo: invoice.customerPhone || 'N/A',
          date: new Date(invoice.invoiceDate).toLocaleDateString(),
          totalAmount: invoice.subTotal || 0,
          vatAmount: invoice.vatAmt || 0,
          grandTotal: invoice.totalAmt || 0,
          balanceAmount: invoice.balanceAmt || 0,
          paymentStatus: activeTab === 1 ? 'Hold' : (invoice.paymentStatus ? invoice.paymentStatus.charAt(0).toUpperCase() + invoice.paymentStatus.slice(1) : 'Pending'),
          _id: invoice._id,
          customerType: invoice.customerType,
          invoiceNo: invoice.invoiceNo,
          invoiceDate: invoice.invoiceDate,
          customerName: invoice.customerName,
          zatcaStatus: invoice.zatcaStatus || null // Add ZATCA status
        }));
        
        setInvoices(formattedInvoices);
        setTotalInvoices(response.data.pagination.total);
        
        // Store phase2Enabled flag from response
        if (response.data.phase2Enabled !== undefined) {
          setPhase2Enabled(response.data.phase2Enabled);
        }
      } else {
        enqueueSnackbar('Failed to fetch invoices', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      enqueueSnackbar('Error loading invoices: ' + (error.response?.data?.message || error.message), { variant: 'error' });
      setInvoices([]);
      setTotalInvoices(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, filters, activeTab, enqueueSnackbar]);

  // Update useEffect to use the memoized fetchInvoices
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Debounce search input
  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => {
      setSearchQuery(value);
      setPage(0);
    }, 300),
    []
  );

  // Memoize event handlers
  const handleSearchChange = useCallback((event) => {
    debouncedSetSearchQuery(event.target.value);
  }, [debouncedSetSearchQuery]);

  const handleFilterClick = useCallback((event) => {
    setFilterAnchorEl(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const handleFilterChange = useCallback((category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...value,
      },
    }));
    setPage(0);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleConvertDraftInvoice = (invoice) => {
    try {
      setLoading(true);
      const invoiceNo = invoice.id;
      
      if (!invoiceNo) {
        enqueueSnackbar('Invoice number is missing', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      api.get(`/draft-invoices/${invoiceNo}`)
        .then(response => {
          if (response.data.success) {
            navigate(`/sales/edit-invoice/${invoiceNo}`, { 
              state: { 
                draftInvoiceId: invoiceNo,
                fromDraft: true
              } 
            });
            enqueueSnackbar('Opening draft invoice', { variant: 'info' });
          } else {
            enqueueSnackbar('Draft invoice not found', { variant: 'error' });
          }
        })
        .catch(error => {
          enqueueSnackbar('Error checking draft invoice', { variant: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      enqueueSnackbar('Error opening draft invoice', { variant: 'error' });
      setLoading(false);
    }
  };

  const handlePrintInvoice = async (invoice) => {
    if (!invoice) return;
    
    try {
      setLoading(true);
      const invoiceNo = invoice.invoiceNo || invoice.id;
      
      let printType = 'thermal';
      if (invoice.customerType === 'B2B') {
        printType = 'thermal';
      } else {
        const settingsResponse = await settingsService.getSettings();
        const settings = settingsResponse?.data || settingsResponse;
        printType = settings?.general?.defaultB2Cprintsize || 'thermal';
      }
      
      const printEndpoint = activeTab === 1 
        ? `/print/draft/${invoiceNo}?type=${printType}&showPrintRemark=true` 
        : `/print/sales/${invoiceNo}?type=${printType}&showPrintRemark=true`;
      
      const response = await api.get(printEndpoint);
      const { html, css } = response.data;
      
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice ${invoiceNo}</title>
            <meta charset="utf-8">
            <style>
              ${css}
            </style>
          </head>
          <body>
            ${html}
            <script>
              window.onload = function() {
                window.print();
                window.close();
              }
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      enqueueSnackbar('Print window opened successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Print failed: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice, format) => {
    if (!invoice) return;
    try {
      setLoading(true);
      const invoiceNo = invoice.invoiceNo || invoice.id;
      
      const downloadEndpoint = activeTab === 1 
        ? `/print/draft/${invoiceNo}?type=${format}` 
        : `/print/sales/${invoiceNo}?type=${format}`;
      
      const response = await api.get(downloadEndpoint);
      
      let blob;
      let extension = 'pdf'; // Default fallback
      if (format === 'xml') extension = 'xml';
      
      if (response.data && typeof response.data === 'object' && !(response.data instanceof Blob)) {
        // API returned JSON instead of raw XML/PDF file. Properly stringify it.
        const jsonString = JSON.stringify(response.data, null, 2);
        blob = new Blob([jsonString], { type: 'application/json' });
        // Use .json extension to prevent XML parser errors in browser
        extension = 'json';
      } else {
        blob = new Blob([response.data]);
      }
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNo}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar(`Downloaded successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(`Download failed: ${error.response?.data?.message || error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPrint = async () => {
    if (!selectedInvoiceToPrint) return;
    
    try {
      setLoading(true);
      const invoiceNo = selectedInvoiceToPrint.invoiceNo || selectedInvoiceToPrint.id;
      
      let printType = 'thermal';
      if (selectedInvoiceToPrint.customerType === 'B2B') {
        printType = 'thermal';
      } else {
        const settingsResponse = await settingsService.getSettings();
        const settings = settingsResponse?.data || settingsResponse;
        printType = settings?.general?.defaultB2Cprintsize || 'thermal';
      }
      
      const printEndpoint = activeTab === 1 
        ? `/print/draft/${invoiceNo}?type=${printType}&showPrintRemark=true` 
        : `/print/sales/${invoiceNo}?type=${printType}&showPrintRemark=true`;
      
      const response = await api.get(printEndpoint);
      const { html, css } = response.data;
      
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice ${invoiceNo}</title>
            <meta charset="utf-8">
            <style>
              ${css}
            </style>
          </head>
          <body>
            ${html}
            <script>
              window.onload = function() {
                window.print();
                window.close();
              }
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      enqueueSnackbar('Print window opened successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Print failed: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
      setPrintConfirmOpen(false);
    }
  };

  const handleClosePrintConfirm = () => {
    setPrintConfirmOpen(false);
    setSelectedInvoiceToPrint(null);
  };

  const handleDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteInvoice = () => {
    if (!invoiceToDelete) return;
    
    const idToDelete = invoiceToDelete.id;
    
    // Close dialog immediately to prevent double clicks and provide immediate feedback
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
    setLoading(true);
    
    api.delete(`/draft-invoices/${idToDelete}`)
      .then(response => {
        if (response.data.success) {
          enqueueSnackbar('Draft invoice deleted successfully', { variant: 'success' });
          fetchInvoices();
        } else {
          enqueueSnackbar(response.data.message || 'Failed to delete draft invoice', { variant: 'error' });
        }
      })
      .catch(error => {
        enqueueSnackbar(
          error.response?.data?.message || error.message || 'Error deleting draft invoice', 
          { variant: 'error' }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const cancelDeleteInvoice = () => {
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
  };

  const handleViewInvoice = async (invoice) => {
    setViewInvoiceLoading(true);
    setSelectedInvoice(null);
    setViewInvoiceDialog(true);
    
    try {
      // Fetch payment methods to resolve IDs to names
      let paymentMethods = [];
      try {
        const pmResponse = await api.get('/masters/payment-methods');
        if (pmResponse.data && Array.isArray(pmResponse.data)) {
          paymentMethods = pmResponse.data;
        }
      } catch (pmError) {
        console.warn('Could not fetch payment methods:', pmError);
      }
      
      // Helper function to resolve payment method ID to name
      const resolvePaymentMethod = (payment) => {
        if (!payment) return payment;
        
        // If payment.method or payment.paymentMethod exists, try to resolve it
        const methodId = payment.paymentMethod || payment.method;
        if (methodId && paymentMethods.length > 0) {
          const method = paymentMethods.find(pm => pm._id === methodId || pm.name === methodId);
          if (method) {
            return {
              ...payment,
              paymentMethod: method.name,
              method: method.name,
              methodName: method.name
            };
          }
        }
        return payment;
      };
      
      if (typeof invoice === 'object' && invoice !== null) {
        if (invoice._id || invoice.id || invoice.invoiceNo) {
          const formattedInvoice = {
            master: {
              invoiceNo: invoice.invoiceNo || invoice.id || invoice._id,
              invoiceDate: invoice.date || invoice.invoiceDate || new Date().toISOString(),
              customerName: invoice.customer || invoice.customerName || 'N/A',
              customerVatNumber: invoice.vatNumber || invoice.customerVatNumber || 'N/A',
              totalAmount: invoice.totalAmount || invoice.totalAmt || invoice.subtotal || 0,
              vatAmount: invoice.vatAmount || invoice.taxAmount || 0,
              grandTotal: invoice.grandTotal || invoice.totalAmt || 0,
              paymentStatus: invoice.paymentStatus || 'N/A',
              balanceAmount: invoice.balanceAmount || invoice.balanceAmt || 0,
              paidAmount: invoice.paidAmount || invoice.paidAmt || 0,
              notes: invoice.notes || '',
            },
            details: Array.isArray(invoice.items) ? invoice.items : [],
            payments: Array.isArray(invoice.payments) ? invoice.payments.map(resolvePaymentMethod) : []
          };
          
          setSelectedInvoice(formattedInvoice);
          
          try {
            const invoiceNo = invoice.id || invoice.invoiceNo;
            if (invoiceNo) {
              const response = await api.get(`/sales/${invoiceNo}`);
              if (response.data.success && response.data.data) {
                // Resolve payment methods in the fetched data as well
                const invoiceData = response.data.data;
                if (invoiceData.payments && Array.isArray(invoiceData.payments)) {
                  invoiceData.payments = invoiceData.payments.map(resolvePaymentMethod);
                }
                setSelectedInvoice(invoiceData);
              }
            }
          } catch (fetchError) {
            console.warn('Could not fetch complete invoice data:', fetchError);
          }
        } else {
          enqueueSnackbar('Error: Cannot identify the invoice', { variant: 'error' });
        }
      } else {
        const response = await api.get(`/sales/${invoice}`);
        if (response.data.success) {
          const invoiceData = response.data.data;
          // Resolve payment methods
          if (invoiceData.payments && Array.isArray(invoiceData.payments)) {
            invoiceData.payments = invoiceData.payments.map(resolvePaymentMethod);
          }
          setSelectedInvoice(invoiceData);
        } else {
          enqueueSnackbar('Failed to fetch invoice details', { variant: 'error' });
        }
      }
    } catch (error) {
      enqueueSnackbar('Error loading invoice details: ' + (error.response?.data?.message || error.message), { variant: 'error' });
    } finally {
      setViewInvoiceLoading(false);
    }
  };
  
  const handleCloseViewDialog = () => {
    setViewInvoiceDialog(false);
  };

  const handlePayBalance = (invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedInvoiceForPayment(null);
  };

  const handleSubmitPayment = async (paymentData) => {
    try {
      setLoading(true);
      const response = await api.post('/sales/payment', paymentData);
      
      if (response.data.success) {
        enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
        handleClosePaymentDialog();
        fetchInvoices(); // Refresh the invoice list
      } else {
        enqueueSnackbar(response.data.message || 'Failed to record payment', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Error recording payment',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResubmitZatca = async (invoice) => {
    try {
      const invoiceNo = invoice.invoiceNo || invoice.id;
      setResubmittingInvoiceId(invoiceNo);
      
      const response = await api.get(`/sales/${invoiceNo}/resubmit-zatca`);
      if (response.data.success) {
        enqueueSnackbar('Invoice resubmitted to ZATCA successfully', { variant: 'success' });
        fetchInvoices(); // Refresh the list
      } else {
        enqueueSnackbar(response.data.message || 'Failed to resubmit to ZATCA', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error resubmitting to ZATCA:', error);
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Error resubmitting to ZATCA',
        { variant: 'error' }
      );
    } finally {
      setResubmittingInvoiceId(null);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <SalesHeader
        exportData={getExportData}
        exportColumns={exportColumns}
        onNewInvoice={() => navigate('/sales/new-invoice')}
      />

      <SalesTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <SalesFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        filterAnchorEl={filterAnchorEl}
        onFilterClick={handleFilterClick}
        onFilterClose={handleFilterClose}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <SalesTable
        invoices={invoices}
        page={page}
        rowsPerPage={rowsPerPage}
        totalInvoices={totalInvoices}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onViewInvoice={handleViewInvoice}
        onPrintInvoice={handlePrintInvoice}
        onConvertDraftInvoice={handleConvertDraftInvoice}
        onDeleteInvoice={handleDeleteInvoice}
        onPayBalance={handlePayBalance}
        onDownloadInvoice={handleDownloadInvoice}
        onResubmitZatca={handleResubmitZatca}
        resubmittingInvoiceId={resubmittingInvoiceId}
        activeTab={activeTab}
        phase2Enabled={phase2Enabled}
      />

      <SalesDialogs
        viewInvoiceDialog={viewInvoiceDialog}
        onCloseViewDialog={handleCloseViewDialog}
        selectedInvoice={selectedInvoice}
        viewInvoiceLoading={viewInvoiceLoading}
        deleteDialogOpen={deleteDialogOpen}
        onCancelDelete={cancelDeleteInvoice}
        onConfirmDelete={confirmDeleteInvoice}
        invoiceToDelete={invoiceToDelete}
        printConfirmOpen={printConfirmOpen}
        onClosePrintConfirm={handleClosePrintConfirm}
        onConfirmPrint={handleConfirmPrint}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        invoice={selectedInvoiceForPayment}
        onSubmit={handleSubmitPayment}
      />
    </Container>
  );
}

export default React.memo(Sales);